import React, {useEffect, useRef, useState} from "react";
import {
	Attachment,
	RecordAttachment,
	RecordAttachmentKindEnum,
	RecordAttachmentRecordTypeEnum,
	RecordAttachmentRestControllerApiFactory
} from "arteco-api-client-ts";
import {
	Box,
	Button,
	ButtonBase,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	IconButton,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TextField,
	Tooltip,
	Typography,
	useMediaQuery,
	useTheme
} from "@mui/material";
import AttachFileRoundedIcon from "@mui/icons-material/AttachFileRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import FolderOpenRoundedIcon from "@mui/icons-material/FolderOpenRounded";
import UploadFileRoundedIcon from "@mui/icons-material/UploadFileRounded";
import {useSnackbar} from "notistack";
import {useApiManager} from "../utils/api";
import {useConfirm} from "../utils/dialogs";
import {dateToString} from "../utils/dates";
import {fileLocalSave, fileToAttach} from "../form/form_inputs/form_file";
import {btnSx, EmptyState, ListCard, Panel, SkeletonRows, SoftChip, tableContainerSx, tableSx, Tone} from "../kit";

/**
 * Documentos anexos a un registro que no tiene adjuntos propios (#14122): la propuesta tal como se
 * envió, la copia firmada, los papeles que manda el cliente. Quién los ve lo decide el API por el
 * tipo de registro; aquí solo se pintan.
 */

const KIND_LABELS: Record<RecordAttachmentKindEnum, string> = {
	SENT: "Enviado",
	SIGNED: "Firmado",
	RECEIVED: "Recibido del cliente",
	OTHER: "Otro",
};

const KIND_TONES: Record<RecordAttachmentKindEnum, Tone> = {
	SENT: "info",
	SIGNED: "success",
	RECEIVED: "neutral",
	OTHER: "neutral",
};

const KINDS = Object.keys(KIND_LABELS) as RecordAttachmentKindEnum[];

const fileSize = (a?: Attachment): string => {
	const bytes = a?.contentSize ?? a?.length;
	if (bytes === undefined || bytes === null) return "";
	if (bytes < 1024) return bytes + " B";
	return bytes < 1024 * 1024 ? (bytes / 1024).toFixed(0) + " KB" : (bytes / 1024 / 1024).toFixed(1) + " MB";
};

/** Hoy en la hora local: `toISOString` da la fecha en UTC, que pasada la medianoche es la de ayer. */
const today = () => {
	const d = new Date();
	return d.getFullYear() + "-" + String(d.getMonth() + 1).padStart(2, "0") + "-" + String(d.getDate()).padStart(2, "0");
};

interface RecordAttachmentsProps {
	recordType: RecordAttachmentRecordTypeEnum;
	recordId?: number;
}

export const RecordAttachments = ({recordType, recordId}: RecordAttachmentsProps) => {
	const apiManager = useApiManager();
	const ctrl = apiManager.factory(RecordAttachmentRestControllerApiFactory);
	const theme = useTheme();
	const compact = useMediaQuery(theme.breakpoints.down("md"));
	const {confirm, confirmDialog} = useConfirm();

	const [items, setItems] = useState<RecordAttachment[] | undefined>(undefined);
	const [adding, setAdding] = useState(false);

	const load = () => {
		if (!recordId) return;
		apiManager.execute(ctrl.listRecordAttachment(recordType, recordId))
			.then(list => setItems(list || []))
			.catch(() => setItems([]));
	};

	useEffect(load, [recordType, recordId]);

	if (!recordId) return null;

	const download = (a: RecordAttachment) =>
		apiManager.executeRaw(ctrl.viewRecordAttachment(a.id as number)).then(resp => fileLocalSave(resp));

	const remove = async (a: RecordAttachment) => {
		if (await confirm({
			title: "Eliminar anexo",
			message: <>Se eliminará «{a.title}» ({a.attachment?.filename}) de este registro.</>,
			confirmLabel: "Eliminar",
			danger: true
		})) {
			apiManager.execute(ctrl.deleteOrDismissRecordAttachment(a.id as number), "Anexo eliminado").then(load);
		}
	};

	const addButton = (
		<Button variant="outlined" size="small" sx={btnSx} startIcon={<UploadFileRoundedIcon/>} onClick={() => setAdding(true)}>
			Anexar documento
		</Button>
	);

	const actions = (a: RecordAttachment) => (
		<Stack direction="row" spacing={0.5} justifyContent="flex-end">
			<Tooltip title="Descargar">
				<IconButton size="small" aria-label={"Descargar " + a.title} onClick={() => download(a)}>
					<DownloadRoundedIcon fontSize="small"/>
				</IconButton>
			</Tooltip>
			<Tooltip title="Eliminar">
				<IconButton size="small" aria-label={"Eliminar " + a.title} onClick={() => remove(a)}>
					<DeleteOutlineRoundedIcon fontSize="small"/>
				</IconButton>
			</Tooltip>
		</Stack>
	);

	const fileLine = (a: RecordAttachment) => (
		<Typography variant="caption" color="text.secondary" component="div" sx={{wordBreak: "break-all"}}>
			{[a.attachment?.filename, fileSize(a.attachment)].filter(Boolean).join(" · ")}
		</Typography>
	);

	let body;
	if (items === undefined) {
		body = <SkeletonRows rows={2}/>;
	} else if (items.length === 0) {
		body = <EmptyState icon={<FolderOpenRoundedIcon/>} title="Sin documentos anexos"
						   description="Anexa aquí la propuesta tal como se envió, la copia firmada o lo que mande el cliente."/>;
	} else if (compact) {
		body = (
			<Stack spacing={1.5}>
				{items.map(a => (
					<ListCard key={a.id}>
						<Stack direction="row" spacing={1} justifyContent="space-between" alignItems="flex-start">
							<Box sx={{minWidth: 0}}>
								<Typography variant="body2" fontWeight={600}>{a.title}</Typography>
								{fileLine(a)}
								<Stack direction="row" spacing={1} alignItems="center" sx={{mt: 1}}>
									<SoftChip label={KIND_LABELS[a.kind]} tone={KIND_TONES[a.kind]}/>
									{a.issueDate && <Typography variant="caption" color="text.secondary">{dateToString(a.issueDate)}</Typography>}
								</Stack>
							</Box>
							{actions(a)}
						</Stack>
					</ListCard>
				))}
			</Stack>
		);
	} else {
		body = (
			<TableContainer sx={tableContainerSx}>
				<Table size="small" sx={tableSx}>
					<TableHead>
						<TableRow>
							<TableCell>Documento</TableCell>
							<TableCell>Tipo</TableCell>
							<TableCell>Fecha</TableCell>
							<TableCell>Origen</TableCell>
							<TableCell/>
						</TableRow>
					</TableHead>
					<TableBody>
						{items.map(a => (
							<TableRow key={a.id}>
								<TableCell>
									<Typography variant="body2" fontWeight={600}>{a.title}</Typography>
									{fileLine(a)}
								</TableCell>
								<TableCell><SoftChip label={KIND_LABELS[a.kind]} tone={KIND_TONES[a.kind]}/></TableCell>
								<TableCell sx={{whiteSpace: "nowrap"}}>{a.issueDate ? dateToString(a.issueDate) : "—"}</TableCell>
								<TableCell>
									<Typography variant="caption" color="text.secondary" sx={{wordBreak: "break-all"}}>{a.source || "—"}</Typography>
								</TableCell>
								<TableCell align="right">{actions(a)}</TableCell>
							</TableRow>
						))}
					</TableBody>
				</Table>
			</TableContainer>
		);
	}

	return (
		<>
			<Panel title="Anexos" icon={<AttachFileRoundedIcon fontSize="small" color="action"/>}
				   subtitle="Documentos enviados, firmados o recibidos de este registro"
				   action={addButton}>
				{body}
			</Panel>
			{adding && (
				<AddAttachmentDialog recordType={recordType} recordId={recordId}
									 onClose={(saved) => {
										 setAdding(false);
										 if (saved) load();
									 }}/>
			)}
			{confirmDialog}
		</>
	);
};

const AddAttachmentDialog = ({recordType, recordId, onClose}: {
	recordType: RecordAttachmentRecordTypeEnum,
	recordId: number,
	onClose: (saved: boolean) => void
}) => {
	const apiManager = useApiManager();
	const ctrl = apiManager.factory(RecordAttachmentRestControllerApiFactory);
	const {enqueueSnackbar} = useSnackbar();
	const fileInput = useRef<HTMLInputElement>(null);

	const [title, setTitle] = useState("");
	const [kind, setKind] = useState<RecordAttachmentKindEnum>("SENT");
	const [issueDate, setIssueDate] = useState(today());
	const [source, setSource] = useState("");
	const [attachment, setAttachment] = useState<Attachment | undefined>(undefined);
	const [saving, setSaving] = useState(false);

	const pick = (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		if (file) {
			fileToAttach(file, enqueueSnackbar).then(a => {
				setAttachment(a);
				if (!title) setTitle(file.name.replace(/\.[^.]+$/, ""));
			});
		}
	};

	const save = () => {
		setSaving(true);
		apiManager.execute(ctrl.saveRecordAttachment({
			recordType, recordId, title: title.trim(), kind,
			issueDate: issueDate || undefined,
			source: source.trim() || undefined,
			attachment
		} as RecordAttachment), "Documento anexado")
			.then(() => onClose(true))
			.finally(() => setSaving(false));
	};

	return (
		<Dialog open onClose={() => onClose(false)} fullWidth maxWidth="sm" PaperProps={{sx: {borderRadius: 3}}}>
			<DialogTitle sx={{fontWeight: 700}}>Anexar documento</DialogTitle>
			<DialogContent>
				<Stack spacing={2.5} sx={{pt: 1}}>
					<Box>
						<input ref={fileInput} type="file" hidden onChange={pick} data-testid="record-attachment-file"/>
						<Button variant="outlined" sx={btnSx} startIcon={<AttachFileRoundedIcon/>} onClick={() => fileInput.current?.click()}>
							{attachment ? "Cambiar fichero" : "Elegir fichero"}
						</Button>
						{attachment && (
							<Typography variant="caption" color="text.secondary" component="div" sx={{mt: 1, wordBreak: "break-all"}}>
								{attachment.filename} · {fileSize(attachment)}
							</Typography>
						)}
					</Box>
					<TextField label="Título" value={title} onChange={e => setTitle(e.target.value)} required fullWidth/>
					<Box>
						<Typography variant="body2" fontWeight={600} sx={{mb: 1}}>Tipo</Typography>
						<Box role="radiogroup" sx={{display: "grid", gap: 1, gridTemplateColumns: {xs: "minmax(0, 1fr) minmax(0, 1fr)", sm: "repeat(4, minmax(0, 1fr))"}}}>
							{KINDS.map(k => (
								<ButtonBase key={k} role="radio" aria-checked={kind === k} onClick={() => setKind(k)}
											sx={{
												p: 1.25, borderRadius: 2, border: "1px solid", justifyContent: "flex-start", textAlign: "left",
												borderColor: kind === k ? "primary.main" : "divider",
												bgcolor: kind === k ? "action.selected" : "background.paper",
												typography: "body2", fontWeight: kind === k ? 700 : 500,
											}}>
									{KIND_LABELS[k]}
								</ButtonBase>
							))}
						</Box>
					</Box>
					<TextField label="Fecha" type="date" value={issueDate} onChange={e => setIssueDate(e.target.value)}
							   InputLabelProps={{shrink: true}} fullWidth/>
					<TextField label="Origen (opcional)" value={source} onChange={e => setSource(e.target.value)} fullWidth
							   helperText="Si se generó: repositorio, ruta del fuente y commit"/>
				</Stack>
			</DialogContent>
			<DialogActions sx={{px: 3, pb: 2}}>
				<Button sx={btnSx} onClick={() => onClose(false)}>Cancelar</Button>
				<Button variant="contained" sx={btnSx} disabled={!attachment || !title.trim() || saving} onClick={save}>
					Anexar
				</Button>
			</DialogActions>
		</Dialog>
	);
};
